import { ExcelProcessorUtils } from '@/utils/ExcelProcessorUtils';
import { TaskSetProfileProcessor } from '@/utils/TaskSetProfileProcessor';
import { ExcelProcessorValidation } from '@/utils/ExcelProcessorValidation';
import { buildNewTaskLock, echoExistingLock } from '@/utils/TaskLockUtils';
import { toRaw } from 'vue';
import { yieldToMain } from '@/utils/mainThreadScheduler';
import { useScheduleStore } from '@/stores/schedule';

export class ExcelProcessor {
  constructor(selectedSchedule, selectedTimeRangeTemplate, taskSetMode = 'all', existingTasks = []) {
    this.scheduleStore = useScheduleStore();
    this.selectedSchedule = selectedSchedule;
    this.selectedTimeRangeTemplate = selectedTimeRangeTemplate;
    this.taskSetMode = taskSetMode; // 'all' or 'parentOnly'
    this.taskMap = new Map();
    this.wbsCounters = {};
    this.taskSetsToGenerate = {};
    this.taskSetsPayload = {};
    this.currentPath = [];
    this.sortOrdersByLevel = new Map();
    this.errors = [];
    this.autoGenerateWBS = false;
    this.autoWBSCounters = {};

    this.excelUtils = new ExcelProcessorUtils(selectedSchedule, selectedTimeRangeTemplate);
    // toRaw: skip reactive-proxy overhead on the per-row existing-task reads.
    this.existingTasksById = this.excelUtils.createTaskMap(toRaw(existingTasks) || []);

    this.taskSetProfileProcessor = new TaskSetProfileProcessor(selectedSchedule);
    this.validation = new ExcelProcessorValidation();
  }


  getColumnIndices(headers) {
    const normalizedHeaders = headers.map(header => header?.toString().trim().toLowerCase());
    
    const viewableLocationColumns = [];
    const taskLevelColumns = [];
    normalizedHeaders.forEach((header, index) => {
      if (header && header.startsWith('viewable location')) {
        viewableLocationColumns.push(index);
      }
      if (header && header.startsWith('task level')) {
        taskLevelColumns.push(index);
      }
    });
    
    return {
      wbsIndex: normalizedHeaders.findIndex(header => header === 'wbs'),
      taskLevelColumns: taskLevelColumns,
      startDateColIndex: normalizedHeaders.findIndex(header => header === 'start date'),
      durationColIndex: normalizedHeaders.findIndex(header => header === 'duration'),
      progressColIndex: normalizedHeaders.findIndex(header => header === 'progress'),
      taskIdIndex: normalizedHeaders.findIndex(header => header === 'task id'),
      taskSetIndex: normalizedHeaders.findIndex(header => header === 'task set'),
      calendarIndex: normalizedHeaders.findIndex(header => header === 'calendar override' || header === 'calendar'),
      viewableLocationColumns: viewableLocationColumns
    };
  }

  processRow(row, columnIndices) {
    const {
      wbsIndex,
      taskLevelColumns,
      startDateColIndex,
      durationColIndex,
      taskIdIndex,
      taskSetIndex,
      calendarIndex,
      viewableLocationColumns
    } = columnIndices;

    if (!this.validation.validateRow(row, columnIndices, this.currentPath, this.autoGenerateWBS)) {
      return { currentTask: null, createdTasks: [] };
    }

    let currentTask = null;
    let createdTasks = [];

    let title = '';
    let level = -1;

    for (let i = taskLevelColumns.length - 1; i >= 0; i--) {
      if (row[taskLevelColumns[i]]) {
        title = row[taskLevelColumns[i]].toString().trim();
        level = i;
        break;
      }
    }

    let wbsCode;
    if (this.autoGenerateWBS) {
      // Generate WBS based on hierarchy level
      const parentWBSKey = this.currentPath.slice(0, level).join('.');

      if (!this.autoWBSCounters[parentWBSKey]) {
        this.autoWBSCounters[parentWBSKey] = 0;
      }

      this.autoWBSCounters[parentWBSKey]++;
      wbsCode = this.autoWBSCounters[parentWBSKey];
    } else {
      wbsCode = row[wbsIndex];
    }

    const newCurrentPath = [...this.currentPath.slice(0, level), wbsCode.toString()];
    const parentWBS = newCurrentPath.slice(0, -1);
    const parentWBSString = parentWBS.join('');
    const parentId = parentWBSString ? this.taskMap.get(parentWBSString)?.id : null;
  
    currentTask = this.createTask({
      wbsCode,
      title,
      parentId,
      parentWBS,
      level,
      row,
      startDateColIndex,
      durationColIndex,
      taskIdIndex,
      taskSetIndex,
      calendarIndex,
      viewableLocationColumns,
      newCurrentPath
    });
  
    if (currentTask) {
      createdTasks.push(currentTask);
      this.taskMap.set(newCurrentPath.join(''), currentTask);

      if (parentId) {
        const parentTask = this.taskMap.get(parentWBSString);
        if (parentTask) {
          parentTask.childTaskIds.push(currentTask.id);
        }
      }
    }
  
    this.currentPath = newCurrentPath;
  
    return {
      currentTask,
      createdTasks
    };
  }

  createTask({
    wbsCode,
    title,
    parentId,
    parentWBS,
    level,
    row,
    startDateColIndex,
    durationColIndex,
    taskIdIndex,
    taskSetIndex,
    calendarIndex,
    viewableLocationColumns,
    newCurrentPath
  }) {
    const hasExplicitTaskId = row[taskIdIndex] !== undefined && row[taskIdIndex] !== '';
    const taskId = hasExplicitTaskId
      ? row[taskIdIndex]
      : this.excelUtils.generateNewId();

    // If the spreadsheet contains an explicit task ID, it must belong to this project
    if (hasExplicitTaskId && Object.keys(this.existingTasksById).length > 0) {
      this.validation.validateTaskIdExists(taskId, row.rowIndex, this.existingTasksById);
    }

    const existingTask = this.existingTasksById[taskId];
    
    const currentLevel = level;
    
    if (!this.sortOrdersByLevel.has(currentLevel)) {
      this.sortOrdersByLevel.set(currentLevel, new Map());
    }
    
    const parentWBSKey = JSON.stringify(parentWBS);
    const levelMap = this.sortOrdersByLevel.get(currentLevel);
    if (!levelMap.has(parentWBSKey)) {
      levelMap.set(parentWBSKey, 0);
    }
    
    const sortOrder = levelMap.get(parentWBSKey);
    levelMap.set(parentWBSKey, sortOrder + 1);
    
    const rowStartDate = row[startDateColIndex];
    const rowDuration = row[durationColIndex];

    const timeRanges = this.excelUtils.getTimeRanges(existingTask, rowStartDate, rowDuration);
  
    if (startDateColIndex !== -1 && row[startDateColIndex]) {
      const startDate = this.excelUtils.parseDate(row[startDateColIndex]);
      if (startDate && !this.excelUtils.isTimeRangeUnchanged(existingTask, startDate, row[durationColIndex])) {
        this.excelUtils.processTimeRanges(
          timeRanges,
          startDate,
          row[durationColIndex]
        );
      }
    }
  
    // When the Calendar Override column is present, every row must name a
    // calendar: a blank cell rejects the import rather than silently falling
    // back. Without the column, existing tasks keep their stored calendar and
    // new tasks use the schedule defaults (see getTimeRanges).
    if (calendarIndex !== -1) {
      const calendarCode = row[calendarIndex]?.toString().trim() || '';
      if (!calendarCode) {
        this.validation.addMissingCalendarOverrideError(row.rowIndex || null);
      } else {
        const calendars = this.scheduleStore.getCalendars || [];
        const matchedCalendar = calendars.find(c => c.code === calendarCode);
        if (matchedCalendar) {
          timeRanges.forEach(tr => {
            tr.calendarId = matchedCalendar.id;
            tr.calendarVersionReference = matchedCalendar.versionReference;
          });
        } else {
          this.validation.addCalendarError(calendarCode, row.rowIndex || null);
        }
      }
    }

    const viewableLocationIds = this.excelUtils.processViewableLocations(row, viewableLocationColumns, existingTask);
  
    const taskObject = {
      id: taskId,
      code: existingTask?.code ?? wbsCode.toString(),
      wbs: [...newCurrentPath.map(String)],
      title: title,
      parentTaskId: parentId || '',
      childTaskIds: existingTask?.childTaskIds ? [...existingTask.childTaskIds] : [],
      prerequisiteTaskIds: existingTask?.prerequisiteTaskIds ? [...existingTask.prerequisiteTaskIds] : [],
      scheduleId: this.selectedSchedule.id,
      viewableLocationIds: [...viewableLocationIds],
      meta: existingTask?.meta ? JSON.parse(JSON.stringify(existingTask.meta)) : {},
      key: existingTask?.key || "",
      lock: existingTask
        ? echoExistingLock(existingTask)
        : buildNewTaskLock(this.excelUtils.getTimeRangeTemplateIds()),
      labels: existingTask?.labels ? [...existingTask.labels] : [],
      sort: sortOrder,
      children: [],
      timeRanges: timeRanges,
      tags: existingTask?.tags ? [...existingTask.tags] : [],
      subtasks: existingTask?.subtasks ? [...existingTask.subtasks] : []
    };

    // Originating spreadsheet row, so hierarchy-level validation can point errors
    // at the offending row. UI-only metadata — stripped from the import payload.
    taskObject.rowIndex = row.rowIndex;
  
    if (taskSetIndex !== -1 && row[taskSetIndex]) {
      const taskSetValue = row[taskSetIndex].toString().trim();
      this.taskSetProfileProcessor.processTaskSetProfileForTask(
        taskObject,
        taskSetValue,
        existingTask,
        this.taskSetMode
      );
    }
  
    return taskObject;
  }

  /**
   * Async so the row loop can yield to the event loop between time slices on
   * large spreadsheets. onProgress({ stage, done, total }) reports rows
   * processed.
   */
  async buildNestedTasks(data, onProgress) {
    if (!data || data.length < 2)
      return {
        nestedTasks: [],
        displayTasks: [],
        taskSetsPayload: {},
        isValid: true,
        errors: [],
        groupedErrors: []
      };

    const columnIndices = this.getColumnIndices(data[0]);
    const {
      wbsIndex,
      startDateColIndex,
      durationColIndex
    } = columnIndices;

    const allTasks = [];

    this.validation.clearErrors();

    // Check if WBS column is missing
    const hasExistingTasks = Object.keys(this.existingTasksById).length > 0;
    if (wbsIndex === -1) {
      if (hasExistingTasks) {
        // If there are existing tasks, WBS column is required
        this.validation.addError('WBS column is missing. For schedules with existing tasks, the WBS column is required.');
        return {
          nestedTasks: [],
          displayTasks: [],
          taskSetsPayload: {},
          isValid: false,
          errors: this.validation.getErrors(),
          groupedErrors: this.validation.getGroupedErrors()
        };
      } else {
        // No existing tasks - we can auto-generate WBS
        this.autoGenerateWBS = true;
      }
    }

    // Time-sliced row loop: processRow must stay strictly sequential (WBS
    // counters and the current path depend on row order), so slices are
    // yielded between rows rather than parallelised.
    const totalRows = data.length - 1;
    let sliceStart = performance.now();
    for (let rowIndex = 1; rowIndex < data.length; rowIndex++) {
      const row = data[rowIndex];
      if (!row.some(cell => cell)) continue;

      row.rowIndex = rowIndex + 1;
      const result = this.processRow(row, columnIndices);

      if (result && result.createdTasks.length > 0) {
        result.createdTasks.forEach(task => {
          allTasks.push(task);
        });
      }

      if (performance.now() - sliceStart >= 30) {
        if (onProgress) onProgress({ stage: 'rows', done: rowIndex, total: totalRows });
        await yieldToMain();
        sliceStart = performance.now();
      }
    }
    if (onProgress) onProgress({ stage: 'rows', done: totalRows, total: totalRows });

    // In 'parentOnly' mode, restore the task-set descendants that were skipped
    // on export so they don't look deleted on re-import. We walk the original
    // children tree: anything with taskSetProfileMeta was skipped and needs to
    // come back; anything already in the spreadsheet stays as-is but we still
    // recurse through it in case deeper descendants were skipped.
    if (this.taskSetMode === 'parentOnly') {
      const tasksFromSpreadsheet = [...allTasks];
      const idsInSpreadsheet = new Set(allTasks.map(t => t.id));

      const restoreSkippedDescendants = (originalChildren, parentIdInImport) => {
        if (!originalChildren) return;
        originalChildren.forEach(child => {
          const wasSkipped = !!child.meta?.taskSetProfileMeta?.taskSetProfileId;
          if (wasSkipped && !idsInSpreadsheet.has(child.id)) {
            const clonedChild = JSON.parse(JSON.stringify(child));
            clonedChild.parentTaskId = parentIdInImport;
            // Tasks that predate calendar support can have an empty calendarId,
            // which the API rejects on tracking time ranges — backfill it from
            // the schedule's calendar mapping before the commit.
            (clonedChild.timeRanges || []).forEach(tr => {
              const mapping = this.excelUtils.getCalendarForTimeRange(this.selectedSchedule, tr.timeRangeTemplateId);
              tr.calendarId = this.excelUtils.resolveTimeRangeCalendarId(
                mapping.calendarId || tr.calendarId,
                tr.timeRangeTemplateType
              );
              tr.calendarVersionReference = mapping.calendarVersionReference || tr.calendarVersionReference || null;
            });
            allTasks.push(clonedChild);
            idsInSpreadsheet.add(child.id);
            restoreSkippedDescendants(child.children, clonedChild.id);
          } else if (idsInSpreadsheet.has(child.id)) {
            restoreSkippedDescendants(child.children, child.id);
          }
        });
      };

      tasksFromSpreadsheet.forEach(task => {
        const originalTask = this.existingTasksById[task.id];
        if (originalTask?.children) {
          restoreSkippedDescendants(originalTask.children, task.id);
        }
      });
    }

    // Build the task hierarchy
    if (onProgress) onProgress({ stage: 'hierarchy' });
    await yieldToMain();
    let nestedTasks = this.excelUtils.buildTaskHierarchy([...allTasks]);

    var originalTasks = Object.values(this.existingTasksById);

    // Process parent task dates after the hierarchy is built
    // This will calculate startDate, endDate, and duration for parent tasks based on child tasks
    if (startDateColIndex !== -1 && durationColIndex !== -1) {
      nestedTasks = this.excelUtils.processParentTaskDates(nestedTasks);
    }

    // Validate nested tasks
    await yieldToMain();
    this.validation.validateTasks(nestedTasks, originalTasks);

    const displayTasks = [...allTasks];

    const taskSetsToGenerate = this.taskSetProfileProcessor.getTaskSetsToGenerate();

    // Generate task set children for tasks that have task sets assigned.
    // In 'parentOnly' mode the children were already restored above, so skip
    // generation for any parent whose existing version already had this profile
    // deployed — otherwise we'd produce duplicate children with fresh ids.
    Object.values(taskSetsToGenerate).forEach(({ taskSetProfile, tasks }) => {
      tasks.forEach(task => {
        const existing = this.existingTasksById[task.id];
        if (this.taskSetProfileProcessor.isTaskSetAlreadyDeployed(existing, taskSetProfile, this.taskSetMode)) {
          return;
        }
        const generatedTasks = this.taskSetProfileProcessor.generateTasksFromTaskSetProfile(taskSetProfile.profile, task);
        displayTasks.push(...generatedTasks);
      });
    });
  
    // Build the display task hierarchy
    let displayTaskHierarchy = this.excelUtils.buildTaskHierarchy(displayTasks);

    // Process parent task dates for display tasks as well
    if (startDateColIndex !== -1 && durationColIndex !== -1) {
      displayTaskHierarchy = this.excelUtils.processParentTaskDates(displayTaskHierarchy);
    }

    // Validate display tasks. When task-set generation added nothing, the
    // display tree is content-identical to nestedTasks — every error would be
    // an exact duplicate addError() already collapses, so skip the re-walk.
    if (displayTasks.length !== allTasks.length) {
      this.validation.validateTasks(displayTaskHierarchy, originalTasks);
    }

    return {
      nestedTasks: nestedTasks,
      displayTasks: displayTaskHierarchy,
      taskSetsPayload: this.taskSetProfileProcessor.getTaskSetsPayload(),
      isValid: this.validation.getErrors().length === 0,
      errors: this.validation.getErrors(),
      groupedErrors: this.validation.getGroupedErrors()
    };
  }
}